import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Snackbar } from '@mui/material';
import StartPage from 'ui_patient/pages/MaskSelectorTool/StartPage';
import PatientInformation from './PatientInformation';
import ChooseMaskSizingMethod from 'ui_patient/pages/MaskSelectorTool/ChooseMaskSizingMethod';
import MaskSizing from 'ui_patient/pages/MaskSelectorTool/MaskSizing';
import CompletePage from 'ui_patient/pages/MaskSelectorTool/CompletePage';
import Alert from '@mui/material/Alert';

import getApiClient from 'ui_patient/api/OpenClient';

import { stateHasPrevious, stateHasHelp } from './PageNavigation';
import ScanHelpPage from 'ui_patient/pages/MaskSelectorTool/ScanHelpPage';
import MugshotModelLoader from 'ui_patient/components/mugshot/MugshotModelLoader';

import { isNextGenEnabled } from 'ui_patient/AppSettings';

import NextGenNavigation from 'ui_patient/pages/MaskSelectorTool/nextGenScan/Navigation';
import { LoadModels } from 'mask-sizing';
// import {defaultHeaderBackgroundColor, defaultHeaderLinkColor} from "ui_patient/pages/MaskSelectorTool/Header";
import { defaultHeaderBackgroundColor, defaultHeaderLinkColor } from './colors';

import ErrorPageContent from 'ui_patient/pages/MaskSelectorTool/ErrorPageContent';

import PropTypes from 'prop-types';
import { getThresholdConfig } from 'ui_patient/pages/MaskSelectorTool/nextGenScan/ThresholdConfig';

import Survey from './Survey';
import HipaaConsent from './HipaaConsent';

const steps = {
  start: 'start',
  patient: 'patient',
  hipaaConsent: 'hipaa_consent',
  sizing: 'sizing',
  selection: 'selection',
  complete: 'complete',
  error: 'error',
};

const submitForm = async data => {
  return getApiClient().post(`/mask-surveys`, data);
};

export const hideScrollbar = () => {
  return {
    overflow: 'auto',
    scrollbarWidth: 'none', // Hide the scrollbar for firefox
    '&::-webkit-scrollbar': {
      display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
    },
    '&-ms-overflow-style:': {
      display: 'none', // Hide the scrollbar for IE
    },
  };
};

const thresholdConfig = {
  // Increase dark conditions threshold to be more sensitive to dark lighting
  DARK_CONDITIONS_THRESHOLD: 140,

  // Keep light conditions threshold balanced
  LIGHT_CONDITIONS_THRESHOLD: 125,

  // Increase dark threshold to reject darker images
  DARK_THRESHOLD: 120,

  // Adjust dark percentile to be more strict
  DARK_PERCENTILE: 85,

  // Adjust light threshold to allow brighter images
  LIGHT_THRESHOLD: 180,

  // Adjust light percentile to be more lenient with bright areas
  LIGHT_PERCENTILE: 15,

  // Increase contrast requirements for better image quality
  CONTRAST_FRACTION_THRESHOLD: 0.25,

  // Widen the contrast percentile range for better evaluation
  CONTRAST_LOWER_PERCENTILE: 5,
  CONTRAST_UPPER_PERCENTILE: 95,

  // Maintain edge detection threshold for blur checking
  BLUR_NUM_EDGES_THRESHOLD: 100,
};

const MaskSelectorForm = ({
  handleBackLink,
  handleDecline,
  currentStep,
  setFieldValue,
  isSubmitting,
  handleSubmit,
  handleComplete,
  values,
  termsValid,
  handleChange,
  errors,
  validateForm,
  state,
  setState,
  navService,
  initialState,
  navigateToStep,
  shouldDisplayBackButton,
  shouldDisplayHelpButton,
  shouldDisplayLogo,
  shouldDisplayExpansion,
  setHeaderBgColor,
  setHeaderLinkColor,
  tokenData,
  mugshotModel = null,
  faceMeshModel = null,
  imageCheckFunction = null,
  handleImageCapture,
}) => {
  const { hme_org_code, hme_location_code } = tokenData;

  // The back button and help button display is generally determined by the current
  // page that our page navigation machine is on (PageNavigation.js).  However,
  // the sizing_scan_take_photo page has an additional state machine, and we do
  // not always want to display the help and back buttons so overrides are provided.
  const [backButtonOverride, setBackButtonOverride] = useState(false);
  const [helpButtonOverride, setHelpButtonOverride] = useState(false);

  // In the nextgen_scan_final_prep step we want to remove the logo as part of our animation
  const [logoOverride, setLogoOverride] = useState(false);
  const [expandedOverride, setExpandedOverride] = useState(false);

  const [model, setModel] = useState(mugshotModel);
  const [loadingPromise, setLoadingPromise] = useState(null);

  const [fmModel, setFaceMeshModel] = useState(faceMeshModel);
  const [icFunction, setImageCheckFunction] = useState(imageCheckFunction);
  const [nextGenModelsLoading, setNextGenModelsLoading] = useState(false);

  const clearOverrides = () => {
    // Clear any overrides from the MugShot state machine page. This is for current gen
    if (currentStep !== 'sizing_scan_take_photo') {
      if (backButtonOverride) {
        setBackButtonOverride(false);
      }
      if (helpButtonOverride) {
        setHelpButtonOverride(false);
      }
    }

    if (isNextGenEnabled() && currentStep !== 'nextgen_scan_final_prep') {
      if (logoOverride) {
        setLogoOverride(false);
      }
      if (expandedOverride) {
        setExpandedOverride(false);
      }
    }
  };

  useEffect(() => {
    navService.subscribe(state => {
      if (state.value !== initialState) {
        navigateToStep(state.value);
      }

      let page_meta = state.meta[`pageNav.${currentStep}`];

      clearOverrides();

      let showBackButton = backButtonOverride ? false : stateHasPrevious(state);
      let showHelpButton = helpButtonOverride ? false : stateHasHelp(state);
      let showLogo = !logoOverride;
      let showExpansion = expandedOverride;

      setHeaderBgColor(
        page_meta?.headerBgColor ?? defaultHeaderBackgroundColor
      );
      setHeaderLinkColor(page_meta?.headerLinkColor ?? defaultHeaderLinkColor);
      shouldDisplayLogo(page_meta?.show_logo ?? showLogo);

      shouldDisplayBackButton(showBackButton);
      shouldDisplayHelpButton(showHelpButton);

      shouldDisplayExpansion(showExpansion);
    });
  }, [
    navService,
    shouldDisplayBackButton,
    shouldDisplayHelpButton,
    backButtonOverride,
    helpButtonOverride,
    currentStep,
    initialState,
    navigateToStep,
    logoOverride,
    shouldDisplayLogo,
    expandedOverride,
    shouldDisplayExpansion,
  ]);

  const currentPageHasTransition = stateName => {
    let currentState = navService.state;

    return currentState.nextEvents.indexOf(stateName) >= 0;
  };

  const requestTransition = transitionName => {
    navService.send(transitionName);
  };

  const handleNext = () => {
    navService.send('NEXT');
  };

  const handleManual = e => {
    if (e !== undefined) {
      e.preventDefault();
    }
    navService.send('MANUAL');
  };

  const { t } = useTranslation('pages.MaskSelectorTool.MaskSelectorTool');

  // Localization Strings
  const genericError = t('genericError');
  const tryAgainLaterError = t('tryAgainLaterError');

  // These heights are based on the components in Header.js and Footer.js
  const headerHeight = 53;

  const handleSurveyBack = (e, data) => {};

  return (
    <Box
      minHeight={`calc(100svh - ${headerHeight}px)`}
      sx={{ ...hideScrollbar, overflowX: 'visible' }}
    >
      {currentStep === steps.start && <StartPage handleNext={handleNext} />}
      <>
        {currentStep === steps.patient && (
          <>
            <PatientInformation
              values={values}
              handleChange={handleChange}
              handleNext={handleNext}
              errors={errors}
              validateForm={validateForm}
              isValid={termsValid}
            />
            <MugshotModelLoader
              model={model}
              setModel={setModel}
              loadingPromise={loadingPromise}
              setLoadingPromise={setLoadingPromise}
            />
            <LoadModels
              setFaceMeshModel={setFaceMeshModel}
              setImageCheckFunction={setImageCheckFunction}
              setLoadingPromise={setNextGenModelsLoading}
              thresholdConfig={getThresholdConfig(
                hme_org_code,
                hme_location_code
              )}
            />
          </>
        )}

        {/* {currentStep.startsWith('survey') && ( //commented the survey part as it is not needed in phase 1
          <Survey
            data={values}
            handleComplete={handleNext}
            handleBack={handleBackLink}
            setFieldValue={setFieldValue}
          />
        )} */}

        {currentStep === steps.hipaaConsent && (
          <HipaaConsent
            handleAccept={handleNext}
            handleDecline={handleDecline}
          />
        )}

        {currentStep.startsWith('sizing_scan') && (
          <ChooseMaskSizingMethod
            setFieldValue={setFieldValue}
            handleSubmit={handleSubmit}
            currentStep={currentStep}
            handleNext={handleNext}
            handlePrevious={handleBackLink}
            requestTransition={requestTransition}
            setBackButtonOverride={setBackButtonOverride}
            setHelpButtonOverride={setHelpButtonOverride}
            tokenData={tokenData}
            model={model}
            setModel={setModel}
            modelLoadingPromise={loadingPromise}
            setModelLoadingPromise={setLoadingPromise}
            handleDoManual={handleManual}
          />
        )}

        {currentStep.startsWith('nextgen_scan_') && (
          <NextGenNavigation
            setFieldValue={setFieldValue}
            handleSubmit={handleSubmit}
            handleComplete={handleComplete}
            handleImageCapture={handleImageCapture}
            currentStep={currentStep}
            handleNext={handleNext}
            handlePrevious={handleBackLink}
            requestTransition={requestTransition}
            setBackButtonOverride={setBackButtonOverride}
            setExpandedOverride={setExpandedOverride}
            setLogoOverride={setLogoOverride}
            setHeaderBgColor={setHeaderBgColor}
            tokenData={tokenData}
            faceMeshModel={fmModel}
            imageCheckFunction={icFunction}
            setFaceMeshModel={setFaceMeshModel}
            setImageCheckFunction={setImageCheckFunction}
            nextGenModelsLoading={nextGenModelsLoading}
            setNextGenModelsLoading={setNextGenModelsLoading}
            handleDoManual={handleManual}
            thresholdConfig={thresholdConfig}
          />
        )}

        {/* { currentStep.startsWith("sizing_manual") && (
                    <MaskSizing
                        values={values}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        currentStep={currentStep}
                        handleNext={handleNext}
                        handlePrevious={handleBackLink}
                        currentPageHasTransition={currentPageHasTransition}
                    />
                )} */}

        {currentStep.startsWith('sizing_scan_help') && (
          <ScanHelpPage
            handlePrevious={handleBackLink}
            handleManual={handleManual}
          />
        )}
      </>

      {currentStep === steps.complete && <CompletePage />}

      {currentStep === steps.error && (
        <ErrorPageContent message={genericError} />
      )}

      <Snackbar
        open={state === 'try_again'}
        autoHideDuration={6000}
        onClose={() => setState('processing')}
      >
        <Alert onClose={() => setState('processing')} severity="error">
          {tryAgainLaterError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
MaskSelectorForm.propTypes = {
  handleBackLink: PropTypes.func.isRequired,
  handleImageCapture: PropTypes.func.isRequired,
  currentStep: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
  termsValid: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  validateForm: PropTypes.func.isRequired,
  state: PropTypes.string.isRequired,
  setState: PropTypes.func.isRequired,
  navService: PropTypes.object.isRequired,
  initialState: PropTypes.string.isRequired,
  navigateToStep: PropTypes.func.isRequired,
  shouldDisplayBackButton: PropTypes.func.isRequired,
  shouldDisplayHelpButton: PropTypes.func.isRequired,
  shouldDisplayLogo: PropTypes.func.isRequired,
  shouldDisplayExpansion: PropTypes.func.isRequired,
  setHeaderBgColor: PropTypes.func.isRequired,
  setHeaderLinkColor: PropTypes.func.isRequired,
  tokenData: PropTypes.object.isRequired,
  mugshotModel: PropTypes.object,
  faceMeshModel: PropTypes.object,
  imageCheckFunction: PropTypes.func,
  handleDecline: PropTypes.func,
};

export default MaskSelectorForm;

export { submitForm, steps };
